import { normalized, values } from '../portfolio-heatmap/portfolio-heatmap.model';

export interface SummaryValue { value: number | null; partial: boolean; }

export function extractSummaryRecords(response: any, key: string, fields: any): any[] {
  if (Array.isArray(response)) return response.flatMap(item => extractSummaryRecords(item, key, fields));
  if (!response || typeof response !== 'object') return [];
  if (response[key] !== undefined) return extractSummaryRecords(response[key], key, fields);
  if (response[fields.market] !== undefined || response[fields.year] !== undefined) return [response];
  return ['responseList', 'responseData', 'data'].flatMap(path => extractSummaryRecords(response[path], key, fields));
}

function sumCells(cells: SummaryValue[]): SummaryValue {
  const known = cells.filter(cell => cell.value !== null);
  return {
    value: known.length ? known.reduce((sum, cell) => sum + cell.value!, 0) : null,
    partial: known.length > 0 && (known.length < cells.length || known.some(cell => cell.partial)),
  };
}

export function buildInvestmentReturnSummary(config: any, input: any[]) {
  const fields = config.fields;
  const unique = new Map<string | object, any>();
  input.filter(record => !['TRUE', 'YES', '1'].includes(normalized(record[fields.deleted]))).forEach(record => {
    const identity = record[fields.identity] || record[fields.projectNumber];
    const key = identity ? `${record[fields.source] || ''}:${identity}:${config.recordMode === 'annual' ? record[fields.year] : ''}` : record;
    unique.set(key, record);
  });
  const records = [...unique.values()].filter(record => config.recordMode !== 'annual' || config.years.some((year: any) => String(year.value) === String(record[fields.year]).trim()));
  const groupFor = (record: any) => {
    const markets = values(record[fields.market]).map(normalized);
    const groups = config.markets.filter((market: any) => market.values.some((value: any) => markets.includes(normalized(value)))).map((market: any) => market.id);
    const unknown = !markets.length || markets.some(value => !config.markets.some((market: any) => market.values.map(normalized).includes(value)));
    if (unknown) groups.push(config.unmappedMarket.id);
    return [...new Set<string>(groups)];
  };
  const groups = records.map(record => ({ record, groups: groupFor(record) }));
  const markets = [...config.markets];
  if (groups.some(entry => entry.groups.includes(config.unmappedMarket.id))) markets.push(config.unmappedMarket);
  const rows = markets.map((market: any) => {
    const cells = config.years.map((year: any) => config.metrics.map((metric: any): SummaryValue => {
      const field = metric.fieldsByYear?.[year.value] || metric.field;
      if (!field) return { value: null, partial: false };
      const available = groups.filter(entry => config.recordMode !== 'annual' || String(entry.record[fields.year]).trim() === String(year.value));
      if (!available.length) return { value: null, partial: false };
      const matching = available.filter(entry => entry.groups.includes(market.id));
      if (!matching.length) return { value: 0, partial: false };
      let amount = 0, known = 0, missing = false;
      matching.forEach(entry => {
        const raw = entry.record[field];
        if (raw == null || String(raw).trim() === '' || !Number.isFinite(Number(raw))) { missing = true; return; }
        const divisor = config.marketAllocation === 'equal' ? entry.groups.length : 1;
        amount += Number(raw) / divisor; known++;
      });
      return { value: known ? amount : null, partial: known > 0 && missing };
    }));
    return { ...market, cells, totals: config.metrics.map((_: any, index: number) => sumCells(cells.map((year: SummaryValue[]) => year[index]))) };
  });
  const totals = config.years.map((_: any, index: number) => config.metrics.map((__: any, metricIndex: number) => sumCells(rows.map(row => row.cells[index][metricIndex]))));
  return {
    rows, totals,
    grandTotals: config.metrics.map((_: any, index: number) => sumCells(totals.map((year: SummaryValue[]) => year[index]))),
    recordCount: records.length,
    unavailableMetrics: config.metrics.filter((_: any, index: number) => !rows.some(row => row.cells.some((year: SummaryValue[]) => year[index].value !== null))),
  };
}
